import { Children, isValidElement, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * 自定义下拉（A 方案：Radix / shadcn 风格，配色按项目蓝色调）。
 *
 * 用法和原生 `<select>` 一样：包 `<option value="x">文案</option>`，
 * 但弹层是自己画的 —— 原生 select 的系统弹层没法改样式。
 */

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

function readOptions(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = []
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return
    const props = child.props as { value?: string | number; children?: ReactNode; disabled?: boolean }
    const value = props.value === undefined ? '' : String(props.value)
    const label =
      typeof props.children === 'string' || typeof props.children === 'number'
        ? String(props.children)
        : Children.toArray(props.children).join('')
    out.push({ value, label, disabled: props.disabled })
  })
  return out
}

export default function Select({
  value,
  onChange,
  children,
  className,
  disabled,
  title,
}: {
  value: string | number
  onChange: (value: string) => void
  children: ReactNode
  className?: string
  disabled?: boolean
  title?: string
}) {
  const options = readOptions(children)
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const currentValue = String(value)
  const current = options.find((o) => o.value === currentValue)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current !== null && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className={`sel${open ? ' sel--open' : ''}`} ref={boxRef}>
      <button
        type="button"
        className={`sel__btn${className === undefined ? '' : ` ${className}`}`}
        disabled={disabled}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sel__label">{current?.label ?? currentValue}</span>
        <span className="sel__chev" />
      </button>

      {open && (
        <div className="sel__panel" role="listbox">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === currentValue}
              disabled={o.disabled}
              className={`sel__opt${o.value === currentValue ? ' sel__opt--on' : ''}`}
              onClick={() => {
                onChange(o.value)
                setOpen(false)
              }}
            >
              <span>{o.label}</span>
              {o.value === currentValue && <span className="sel__tick">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
